import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ExternalLink, Clock } from "lucide-react";
import { getCategory, getArticlesByCategory, CATEGORIES } from "@/data/resources";
import { PageHero, Breadcrumbs, FadeIn } from "@/components/Shared";

export default function ResourceCategory() {
  const { category } = useParams();
  const cat = getCategory(category);
  if (!cat) return <Navigate to="/resources" replace />;

  const articles = getArticlesByCategory(category);

  return (
    <main data-testid="resource-category-page">
      <PageHero eyebrow="Legal Resources" title={cat.title} lead={cat.description} />
      <Breadcrumbs items={[{ label: "Legal Resources", href: "/resources" }, { label: cat.title }]} />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          {/* Category switcher */}
          <FadeIn>
            <div className="flex flex-wrap gap-2" data-testid="category-switcher">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/resources/${c.slug}`}
                  className={`rounded-sm border px-5 py-2 text-sm font-semibold uppercase tracking-[0.2em] transition-colors duration-200 ${
                    c.slug === category
                      ? "border-brand-navy bg-brand-navy text-white"
                      : "border-slate-300 text-slate-500 hover:border-brand-gold hover:text-brand-navy"
                  }`}
                  data-testid={`category-tab-${c.slug}`}
                >
                  {c.title}
                </Link>
              ))}
            </div>
          </FadeIn>

          {/* Article list */}
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((a, i) => {
              const card = (
                <article className="brand-card flex h-full flex-col p-7">
                  <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    <Clock className="size-3.5" /> {a.readTime}
                  </p>
                  <h3 className="mt-4 flex-1 text-lg font-medium leading-snug text-brand-navy">
                    {a.title}
                  </h3>
                  <p className="mt-4 text-xs text-slate-500">
                    {a.author} · {a.date}
                  </p>
                  <span className="brand-text-link mt-5 flex items-center gap-2">
                    {a.body ? (
                      <>Read article <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" /></>
                    ) : (
                      <>Read on hallinans.co.uk <ExternalLink className="size-3.5" /></>
                    )}
                  </span>
                </article>
              );
              return (
                <FadeIn key={a.slug} delay={(i % 3) * 0.07} className="h-full">
                  {a.body ? (
                    <Link to={`/resources/${a.category}/${a.slug}`} className="group block h-full" data-testid={`article-card-${a.slug}`}>
                      {card}
                    </Link>
                  ) : (
                    <a href={a.externalUrl} target="_blank" rel="noopener noreferrer" className="group block h-full" data-testid={`article-card-${a.slug}`}>
                      {card}
                    </a>
                  )}
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
